import { useEffect, useMemo } from 'react';

import { useDispatch, useObject, useSelector } from '_common/hooks';
import { setObjectPreview } from './ObjectPreviewSlice';
import { navigateToFile } from 'router/history';
import { toast } from 'utils/toast';

import ObjectPreviewProvider from './ObjectPreviewContext';
import Topbar from './Topbar/Topbar';
import Preview from './Preview/Preview';
import { getValidZipMime } from 'utils';

import styles from './ObjectPreview.module.scss';

const ObjectPreview = () => {
  const dispatch = useDispatch();

  const objectId = useSelector((state) => state.objectPreview.id);

  const { data } = useObject(
    { object_id: objectId ?? '', object_type: 'file' },
    { skip: !objectId },
  );

  const type = useMemo(() => {
    let type: ObjectPreviewType = undefined;
    const zipMime = getValidZipMime(data);

    if (data) {
      switch (zipMime ?? data.mime?.type) {
        case 'image/png':
        case 'image/jpeg':
        case 'image/jpg':
          // add remaining image mime types
          type = 'image';
          break;
        case 'application/pdf':
          type = 'pdf';
          break;
        case 'application/msword':
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          type = 'docx';
          break;
        case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
          type = 'presentation';
          break;
        default:
          navigateToFile(data.id);
          toast({
            variant: 'success',
            title: 'ELEMENT_TYPE_DOWNLOADED',
            titleValues: { elementType: data.type.charAt(0).toUpperCase() + data.type.slice(1) },
            message: 'ELEMENT_TYPE_SUCCESSFULLY_DOWNLOADED',
            messageValues: { elementTitle: data.name },
          });

          dispatch(setObjectPreview({ id: null }));
      }
    }

    return type;
  }, [data]);

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };

  const close = () => {
    dispatch(setObjectPreview({ id: null }));
  };

  if (objectId != null && data?.type === 'file' && type) {
    return (
      <div className={styles.root} id="object-preview-root" data-testid="object-preview-root">
        <ObjectPreviewProvider type={type} object={data}>
          <Topbar />
          <Preview />
        </ObjectPreviewProvider>
      </div>
    );
  }
};

export default ObjectPreview;
